import { CreatedOrderGuide } from '@/experiences/sales-portal/blocs/mutators/use-create-order-guide-from-menu-mutator';
import { MenuImageItem } from '@/experiences/sales-portal/blocs/loaders/use-menu-image-items-loader';
import { useSalesPortalCreateOrderGuideFromMenuItemsMutation } from '@/experiences/sales-portal/blocs/documents/sales-portal-create-order-guide-from-menu-items.generated';

interface CreateOrderGuideFromMenuItemsMutatorParams {
  onError?: () => void;
  restaurantUUID: string;
  supplierUUID: string;
}

interface CreateOrderGuideFromMenuItemsMutatorResult {
  createOrderGuideFromMenuItems: (
    items: MenuImageItem[],
  ) => Promise<CreatedOrderGuide | null>;
}

/**
 * Creates an order guide from the menu items the user reviewed (extracted from
 * an uploaded menu image). On failure it resolves to `null` and invokes
 * `onError`.
 *
 * @param params.onError - Invoked when generation fails; lets the consumer
 * surface the error (e.g. a toast)
 * @param params.restaurantUUID - The UUID of the restaurant to generate for
 * @param params.supplierUUID - The UUID of the supplier
 * @returns `createOrderGuideFromMenuItems`, resolving to the created guide's
 * UUIDs or `null` on failure
 *
 * @example
 * ```ts
 * const { createOrderGuideFromMenuItems } =
 *   useCreateOrderGuideFromMenuItemsMutator({
 *     onError: showErrorToast,
 *     restaurantUUID,
 *     supplierUUID,
 *   });
 * const created = await createOrderGuideFromMenuItems(reviewedItems);
 * if (created) {
 *   navigateToOrderGuide(created.orderGuideCollectionUUID);
 * }
 * ```
 */
export const useCreateOrderGuideFromMenuItemsMutator = ({
  onError,
  restaurantUUID,
  supplierUUID,
}: CreateOrderGuideFromMenuItemsMutatorParams): CreateOrderGuideFromMenuItemsMutatorResult => {
  const [createOrderGuide] =
    useSalesPortalCreateOrderGuideFromMenuItemsMutation();

  const createOrderGuideFromMenuItems = async (
    items: MenuImageItem[],
  ): Promise<CreatedOrderGuide | null> => {
    try {
      const { data } = await createOrderGuide({
        variables: {
          items: items.map((item) => ({
            ingredients: item.ingredients,
            item_name: item.name,
          })),
          restaurantUUID,
          supplierUUID,
        },
      });
      const response = data?.createOrderGuideFromGeneratedMenuItems;
      if (!response) {
        onError?.();
        return null;
      }
      return {
        orderGuideCollectionUUID: response.order_guide_collection_uuid,
        orderGuidePartitionUUID: response.order_guide_partition_uuid,
      };
    } catch {
      onError?.();
      return null;
    }
  };

  return { createOrderGuideFromMenuItems };
};
